import cn from "classnames";
import { Datetime, Field } from "components/shared/form";
import { bidPaymentStatuses, bidPaymentStatusesEnum, featureModuleTypesEnum } from "helpers/enums";
import { enumLabel } from "helpers/system";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover } from "react-tiny-popover";
import { applyBidPayment } from "sources/books/bids/closeBids";

const paymentConfig = {
    paymentStatus: {
        label: "Статус платежа",
        inputProps: { name: "paymentStatus" },
        options: bidPaymentStatuses,
    },
    planPaymentDate: {
        label: "Плановая дата платежа",
        inputProps: { name: "planPaymentDate", type: "date" },
    },
    factPaymentDate: {
        label: "Фактическая дата платежа",
        inputProps: { name: "factPaymentDate", type: "date" },
    },
    remainingPayment: {
        label: "Остаток к оплате",
        inputProps: { name: "remainingPayment", type: "number", step: "1", className: "form-control form-control-xs" },
    },
};

const paymentStatusBadgeMods = {
    [bidPaymentStatusesEnum.NotPaid]: "badge-secondary",
    [bidPaymentStatusesEnum.Paid]: "badge-success",
    [bidPaymentStatusesEnum.PartiallyPaid]: "badge-warning",
};

const getPayment = (bid, bidId) => ({
    ...(bid.payment || {}),
    bidId,
    paymentStatus: bid.payment?.paymentStatus || bidPaymentStatusesEnum.NotPaid,
});

const PaymentStatus = ({ bid, bidId, bidPolicy, dataTestId = "payment-status", profile, refresh, roles }) => {
    const initialPayment = useMemo(() => getPayment(bid, bidId), [bid, bidId]);
    const [opened, setOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [payment, setPayment] = useState(initialPayment);
    const hasAccountsReceivable = profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable);
    const canEdit = hasAccountsReceivable && bidPolicy.canEditPaymentStatusInShow();
    const paymentStatus = payment.paymentStatus || bidPaymentStatusesEnum.NotPaid;
    const label = enumLabel(bidPaymentStatuses, paymentStatus);
    const badgeClassName = cn("badge", paymentStatusBadgeMods[paymentStatus] || "badge-secondary");

    useEffect(() => {
        setPayment(initialPayment);
    }, [initialPayment]);

    const handlePopoverToggle = useCallback(() => {
        if (canEdit && !loading) {
            setOpened((open) => !open);
            setError("");
        }
    }, [canEdit, loading]);

    const handlePopoverClose = useCallback(() => {
        setOpened(false);
        setPayment(initialPayment);
        setError("");
    }, [initialPayment]);

    const handleChange = useCallback(({ name, value }) => {
        if (["planPaymentDate", "factPaymentDate"].includes(name) && value) {
            value = moment(value).format("YYYY-MM-DD");
        }
        const newValue = { [name]: value };
        if (name === "paymentStatus") {
            if (value !== bidPaymentStatusesEnum.PartiallyPaid) {
                newValue.remainingPayment = null;
            }
            if (value !== bidPaymentStatusesEnum.Paid) {
                newValue.factPaymentDate = null;
            }
        }
        setPayment((prev) => ({ ...prev, ...newValue }));
        setError("");
    }, []);

    const savePayment = useCallback(() => {
        if (
            payment.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid &&
            (payment.remainingPayment === null ||
                payment.remainingPayment === undefined ||
                payment.remainingPayment === "")
        ) {
            setError("Укажите остаток к оплате");
            return;
        }

        setLoading(true);
        const { invoiceDate, planPaymentDate, factPaymentDate, paymentStatus, comment, remainingPayment } = payment;
        applyBidPayment({
            bidId,
            payment: {
                comment,
                paymentStatus,
                invoiceDate,
                planPaymentDate,
                factPaymentDate,
                remainingPayment,
            },
        }).then(
            (response) => {
                if (response) {
                    setOpened(false);
                    refresh();
                }
                setLoading(false);
            },
            () => setLoading(false),
        );
    }, [bidId, payment, refresh]);

    const onClickOutside = useCallback(
        (event) => {
            if (opened && !event.target.closest(".react-datepicker")) {
                handlePopoverClose();
            }
        },
        [opened, handlePopoverClose],
    );

    if (roles.isTruckingOrganizationDispatcher) {
        return null;
    }

    if (!hasAccountsReceivable && !bid.payment) {
        return null;
    }

    const popoverContent = (
        <div className={cn("popover w-auto font-size-14", { "loading loading--small": loading })}>
            <div className="font-size-16 pb-2">Изменение статуса платежа</div>
            <div className="form-group">
                <label className="small mb-0">{paymentConfig.paymentStatus.label}</label>
                <select
                    className="form-control form-control-xs"
                    onChange={(event) => handleChange({ name: "paymentStatus", value: event.target.value })}
                    value={payment.paymentStatus}
                >
                    {paymentConfig.paymentStatus.options.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </div>
            <Datetime
                labelWrapper="col-12 small mb-0"
                wrapper="col-12"
                inPortal
                {...paymentConfig.planPaymentDate}
                libOptions={{ className: "form-control form-control-xs" }}
                onChange={handleChange}
                value={payment.planPaymentDate}
            />
            {payment.paymentStatus === bidPaymentStatusesEnum.Paid && (
                <Datetime
                    labelWrapper="col-12 small mb-0"
                    wrapper="col-12"
                    inPortal
                    {...paymentConfig.factPaymentDate}
                    libOptions={{ className: "form-control form-control-xs" }}
                    onChange={handleChange}
                    value={payment.factPaymentDate}
                />
            )}
            {payment.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid && (
                <Field
                    labelWrapper="col-12 small mb-0"
                    fieldWrapper="col-12"
                    {...paymentConfig.remainingPayment}
                    onChange={handleChange}
                    value={payment.remainingPayment ?? ""}
                />
            )}
            {error && <div className="text-danger small mb-2">{error}</div>}
            <div className="d-flex justify-content-between mb-2">
                <div className="btn btn-xs btn-secondary" onClick={handlePopoverClose}>
                    Отмена
                </div>
                <div className="btn btn-xs btn-brand" onClick={savePayment}>
                    Сохранить
                </div>
            </div>
        </div>
    );

    const statusBadge = (
        <>
            <span className={badgeClassName}>{label}</span>
            {payment.isPaymentOverdue && (
                <span className="badge badge-pill badge-overdue ml-1">Просрочен</span>
            )}
        </>
    );

    if (!canEdit) {
        return (
            <span className="show-bid__payment-status" data-test-id={dataTestId}>
                {statusBadge}
            </span>
        );
    }

    return (
        <Popover
            isOpen={opened}
            content={popoverContent}
            containerStyle={{ zIndex: 130 }}
            onClickOutside={onClickOutside}
        >
            <button
                aria-expanded={opened}
                aria-label={`Изменить статус платежа: ${label}`}
                className="show-bid__payment-status show-bid__payment-status--editable"
                data-test-id={dataTestId}
                disabled={loading}
                onClick={handlePopoverToggle}
                type="button"
            >
                {statusBadge}
                <i aria-hidden="true" className="icon-uEA10-pen show-bid__payment-status-icon" />
            </button>
        </Popover>
    );
};

export default PaymentStatus;
